import React, { useState, useEffect } from 'react';
import { useQuery, QueryHookOptions } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import URLSearchParams from '@ungap/url-search-params';

import { MDLPortal } from '../contexts/portal';
import { SetAcquisitionParamsHandler } from './set-acquisition-params-handler';
import { LogoutHandler } from './logout-handler';
import {
  OpenModal,
  CloseModal,
  AuthRegModal,
  ModalEntryLocation,
} from './auth-reg-modal';
import {
  InWellPaywallEntryLocation,
  EnableInWellPaywall,
  DisableInWellPaywall,
} from './in-well-paywall';
import { Auth } from '@mdlinx/shared';
import {
  deleteStoredAcquisitionParams,
  getQueryOrSavedAcquisitionIds,
  getStoredAcquisitionPageviewLimitOrDefault,
} from '../utility/acquisition';
import { storeUtmParams } from '../utility/tracking';
import { MDLinxAuth } from './window-mdlinx-auth';

export const ME_GATEWAY_QUERY = gql`
  query Me {
    me {
      account {
        uid
      }
      acquisitionProfile {
        email
        qualifiedTrafficSource
        lastName
        firstName
      }
      profile {
        firstName
        lastName
        selfReportedUsmd
        specialtyName
        legacySpecialtySlug
      }
    }
  }
`;

type UserAuthState = 'AUTHENTICATED' | 'ANONYMOUS' | 'UNKNOWN';

interface AuthModalState {
  entryLocation: ModalEntryLocation;
  modalIsDismissable: boolean;
  modalIsOpen: boolean;
}
const initialAuthModalState: AuthModalState = {
  entryLocation: '',
  modalIsDismissable: true,
  modalIsOpen: false,
};

interface InWellPaywallState {
  entryLocation: InWellPaywallEntryLocation;
  enabled: boolean;
}
const initialInWellPaywallState: InWellPaywallState = {
  entryLocation: 'REGISTRATION',
  enabled: false,
};

export interface AuthRegContextType {
  authRegModalIsOpen: boolean;
  headerOverlayIsOpen: boolean;
  authState: UserAuthState;
  userProfile: null | {
    firstName: string;
    lastName: string;
    selfReportedUsmd: boolean;
    specialtyName: string;
    legacySpecialtySlug: string;
  };
  acquisitionUserProfile?: {
    email?: string;
    qualifiedTrafficSource?: string;
    firstName?: string;
    lastName?: string;
  };
  closeAuthModal: CloseModal;
  onAuthRejection: () => void;
  openAuthModal: OpenModal;
  setLogoutInProgress: () => void;
  setShowHeaderOverlay: (showOverlay: boolean) => void;
  inWellPaywallState: InWellPaywallState;
  setInWellPaywallState: (state: InWellPaywallState) => void;
  enableInWellPaywall: EnableInWellPaywall;
  disableInWellPaywall: DisableInWellPaywall;
  authBaseUrl: string;
  onLoginSucceeded: OnLoginSucceeded;
}

const initialAuthRegContext: AuthRegContextType = {
  authRegModalIsOpen: false,
  headerOverlayIsOpen: false,
  authState: 'UNKNOWN',
  userProfile: null,
  closeAuthModal: () => {},
  onAuthRejection: () => {},
  openAuthModal: () => {},
  setLogoutInProgress: () => {},
  setShowHeaderOverlay: () => {},
  inWellPaywallState: initialInWellPaywallState,
  setInWellPaywallState: _state => {},
  enableInWellPaywall: () => {},
  disableInWellPaywall: () => {},
  authBaseUrl: '',
  onLoginSucceeded: () => {},
};

export const AuthRegContext = React.createContext<AuthRegContextType>(
  initialAuthRegContext
);

export const AuthRegConsumer = AuthRegContext.Consumer;

type OnLoginSucceeded = (loginType?: string) => void;

interface AuthRegProviderProps {
  children: React.ReactNode;
  authBaseUrl: string;
  inBrowser: boolean; // How we determine this is dependent on the SSR implementation (or absence of one) so make it the responsibility of the consumer
  onLoginSucceeded?: OnLoginSucceeded;
  onLogoutSucceeded?: () => void;
  onLogoutFailed?: () => void;
  inWellPaywall?: boolean;
}

const pageviewCookieName = () => {
  switch (new URL(window.location.href).hostname) {
    case 'qa.mdlinx.com':
      return 'qa-mdl-pv';
    case 'dev.mdlinx.com':
      return 'dev-mdl-pv';
    case 'localhost.mdlinx.com':
      return 'dev-mdl-pv';
    case 'next.c.mdlinx.com':
      return 'dev-mdl-pv';
    case 'qa.dev-c.mdlinx.com':
      return 'dev-mdl-pv';
    default:
      return 'prod-mdl-pv';
  }
};

export const pageviewCountFromCookie = (rawCookie: string) => {
  try {
    const cookieName = pageviewCookieName();
    const encodedPageviewCounts = rawCookie
      .split('; ')
      .find(c => c.startsWith(cookieName))
      ?.split(cookieName + '=')[1];

    // Pageview cookie expects values in a { contentType: 123 } format
    const decodedPageviewCounts: Record<string, number> = encodedPageviewCounts
      ? JSON.parse(atob(encodedPageviewCounts))
      : {};

    const pageviewValues = Object.values(decodedPageviewCounts);
    if (!pageviewValues.every(Number.isInteger)) {
      throw new Error('Invalid values in pageview cookie');
    }

    const summedPageviews = pageviewValues.reduce(
      (acc, pvCount) => acc + pvCount,
      0
    );

    return summedPageviews;
  } catch (e) {
    console.error(e);
    return 0;
  }
};

const useWindowMDLinxAuth = ({
  openModal,
  closeModal,
  enableInWellPaywall,
  disableInWellPaywall,
}: {
  openModal: OpenModal;
  closeModal: CloseModal;
  enableInWellPaywall: EnableInWellPaywall;
  disableInWellPaywall: DisableInWellPaywall;
}) => () => {
  const initialQueue: Function[] =
    (window as any).MDLinxAuth && Array.isArray((window as any).MDLinxAuth.cmd)
      ? (window as any).MDLinxAuth.cmd
      : [];

  const mdlinxAuth = new MDLinxAuth({
    initialQueue,
    openModal,
    closeModal,
    enableInWellPaywall,
    disableInWellPaywall,
  });

  (window as any).MDLinxAuth = mdlinxAuth;
  mdlinxAuth.init(); // MDLinxAuth must be available on window before calling the cmd queue

  return () => {
    const cmd =
      (window as any).MDLinxAuth && (window as any).MDLinxAuth.eject().cmd;
    if (cmd) {
      (window as any).MDLinxAuth = { cmd };
    }
  };
};

export const AuthRegProvider: React.FC<AuthRegProviderProps> = ({
  children,
  authBaseUrl,
  inBrowser,
  onLoginSucceeded: parentOnLoginSucceeded = () => {},
  onLogoutSucceeded: parentOnLogoutSucceeded = () => {},
  onLogoutFailed = () => {},
  inWellPaywall = false,
}) => {
  const [authModalState, setAuthModalState] = useState<AuthModalState>(
    initialAuthModalState
  );
  const [inWellPaywallState, setInWellPaywallState] = useState<
    InWellPaywallState
  >(initialInWellPaywallState);
  const [portalRootNode, setPortalRootNode] = useState<Element | null>(null);
  const [logoutInProgress, setLogoutState] = useState<boolean>(false);
  const [showHeaderOverlay, setShowHeaderOverlay] = useState<boolean>(false);
  const meResponse = useQuery(ME_GATEWAY_QUERY, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  } as QueryHookOptions);
  const isHighlightsPage =
    !!inBrowser &&
    new URL(window.location.href).pathname.split('/')[1] === 'highlights';
  const isSmartestDoc =
    !!inBrowser &&
    `${new URL(window.location.href).origin}/` ===
      process.env.SMARTEST_DOC_BASE_URL;

  !!inBrowser && storeUtmParams('LAST_TOUCH_REGISTRATION_ATTRIBUTION');

  const triggerPaywall = ({
    entryLocation,
    modalIsDismissable = false,
  }: {
    entryLocation: 'REGISTRATION' | 'ACQUISITION' | 'HIGHLIGHTS_ACQUISITION';
    modalIsDismissable?: boolean;
  }) => {
    if (inWellPaywall && !modalIsDismissable) {
      setInWellPaywallState({
        entryLocation,
        enabled: true,
      });
    } else {
      setAuthModalState({
        entryLocation,
        modalIsOpen: true,
        modalIsDismissable,
      });
    }
  };

  useEffect(() => {
    const acquisitionProfile = meResponse?.data?.me?.acquisitionProfile;
    const account = meResponse?.data?.me?.account;
    const acquisitionPageviewLimit = getStoredAcquisitionPageviewLimitOrDefault();
    const pageviewCount = pageviewCountFromCookie(document.cookie);

    const overAcquisitionContentLimit =
      pageviewCount > acquisitionPageviewLimit;

    if (account && inBrowser) {
      // Clear pageview cookie because we're logged in
      document.cookie = `${pageviewCookieName()}= ; domain=.mdlinx.com; Path=/; max-age=0;`;
      deleteStoredAcquisitionParams();
    }

    if (account) {
      // Reset modal state if logged in (e.g. Paywall etc.)
      // If on the a highlights page, do not reset modal as latest designs
      // call for the modal to stay open after being logged in
      const accountActionModal = authModalState.entryLocation === 'AUTH_ACTION';
      if (
        !accountActionModal &&
        !(authModalState.entryLocation === 'HIGHLIGHTS_ACQUISITION') &&
        !(authModalState.entryLocation === 'REGISTRATION')
      ) {
        if (inWellPaywall) {
          setInWellPaywallState(initialInWellPaywallState);
        } else {
          setAuthModalState(initialAuthModalState);
        }
      }
    } else if (
      acquisitionProfile?.email &&
      !isHighlightsPage &&
      !isSmartestDoc &&
      overAcquisitionContentLimit
    ) {
      triggerPaywall({ entryLocation: 'ACQUISITION' });
    } else if (acquisitionProfile?.qualifiedTrafficSource) {
      triggerPaywall({ entryLocation: 'REGISTRATION' });
    }
  }, [meResponse]);

  const userAuthState: UserAuthState = (() => {
    const queryComplete = !!meResponse.data;
    const userAccount = meResponse?.data?.me?.account;

    if (queryComplete && userAccount) {
      return 'AUTHENTICATED';
    }
    if (queryComplete && !userAccount) {
      return 'ANONYMOUS';
    }
    return 'UNKNOWN';
  })();

  const openAuthModal = ({
    entryLocation,
    modalIsDismissable = true,
  }: {
    entryLocation: ModalEntryLocation;
    modalIsDismissable?: boolean;
  }) =>
    setAuthModalState(prevState =>
      prevState.modalIsOpen
        ? {
            entryLocation,
            modalIsDismissable: prevState.modalIsDismissable,
            modalIsOpen: true,
          }
        : { entryLocation, modalIsDismissable, modalIsOpen: true }
    );

  const closeAuthModal = () => setAuthModalState(initialAuthModalState);

  const enableInWellPaywall = ({
    entryLocation,
  }: {
    entryLocation: InWellPaywallEntryLocation;
  }) =>
    setInWellPaywallState({
      ...inWellPaywallState,
      enabled: true,
      entryLocation,
    });
  const disableInWellPaywall = () =>
    setInWellPaywallState(initialInWellPaywallState);

  useEffect(
    useWindowMDLinxAuth({
      openModal: triggerPaywall as any,
      closeModal: closeAuthModal,
      enableInWellPaywall,
      disableInWellPaywall,
    }),
    []
  );

  let acquisitionHandler = null;
  if (inBrowser) {
    const { mdlaq, vndid } = getQueryOrSavedAcquisitionIds() ?? {};

    if (mdlaq && vndid) {
      const searchString = `?${new URLSearchParams({ mdlaq, vndid })}`;

      acquisitionHandler = (
        <SetAcquisitionParamsHandler
          authUrl={`${authBaseUrl}#/set-acquisition-params/${searchString}`}
          onSetAcquisitionParamsFailed={() =>
            console.error('SetAcquisitionParams Failed')
          }
          onSetAcquisitionParamsSucceeded={() => {
            meResponse.refetch().catch(console.error);
          }}
        />
      );
    }
  }

  const onLoginSucceeded = (loginType?: string) => {
    loginType !== Auth.highlightsLoginSucceeded &&
      setAuthModalState(initialAuthModalState);
    void meResponse.refetch();
    parentOnLoginSucceeded(loginType);
  };

  const onLogoutSucceeded = () => {
    void meResponse.refetch();
    parentOnLogoutSucceeded();
  };

  const onAuthRejection = () => meResponse.refetch();
  // Wait until we know that the user is not logged in before opening the modal
  const modalIsOpen =
    (userAuthState === 'ANONYMOUS' && authModalState.modalIsOpen) ||
    (authModalState.entryLocation === 'AUTH_ACTION' &&
      authModalState.modalIsOpen) || // Password help
    (userAuthState === 'AUTHENTICATED' &&
      authModalState.entryLocation === 'HIGHLIGHTS_ACQUISITION' &&
      authModalState.modalIsOpen); // keep modal open when on highlights page

  const inWellPaywallIsEnabled =
    userAuthState === 'ANONYMOUS' && inWellPaywallState.enabled;

  return (
    <MDLPortal.Provider value={portalRootNode}>
      {acquisitionHandler}
      <div ref={setPortalRootNode}>
        <AuthRegModal
          authBaseUrl={authBaseUrl}
          onLoginSucceeded={onLoginSucceeded}
          {...authModalState}
          modalIsOpen={modalIsOpen}
          setAuthModalState={setAuthModalState}
          inBrowser={inBrowser}
        />
        <LogoutHandler
          authBaseUrl={authBaseUrl}
          inProgress={logoutInProgress}
          setInProgress={inProgress => setLogoutState(inProgress)}
          onLogoutSucceeded={onLogoutSucceeded}
          onLogoutFailed={onLogoutFailed}
        />
        <AuthRegContext.Provider
          value={{
            authRegModalIsOpen: modalIsOpen,
            headerOverlayIsOpen: showHeaderOverlay,
            authState: userAuthState,
            userProfile: meResponse?.data?.me?.profile ?? null,
            acquisitionUserProfile:
              meResponse?.data?.me?.acquisitionProfile ?? null,
            closeAuthModal,
            onAuthRejection,
            openAuthModal,
            setLogoutInProgress: () => setLogoutState(true),
            setShowHeaderOverlay,
            inWellPaywallState: {
              entryLocation: inWellPaywallState.entryLocation,
              enabled: inWellPaywallIsEnabled,
            },
            setInWellPaywallState,
            enableInWellPaywall,
            disableInWellPaywall,
            authBaseUrl,
            onLoginSucceeded,
          }}
        >
          {children}
        </AuthRegContext.Provider>
      </div>
    </MDLPortal.Provider>
  );
};
